import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { searchNDISItems } from '../utils/ndisUtils';
import './LandingPage.css';

function useFadeIn() {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const children = el.querySelectorAll('.fade-in');
    children.forEach((child) => observer.observe(child));

    return () => observer.disconnect();
  }, []);

  return ref;
}

function PriceGuideSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);

  const handleSearch = (value) => {
    setQuery(value);
    if (value.length >= 2) {
      setResults(searchNDISItems(value).slice(0, 12));
    } else {
      setResults([]);
    }
  };

  return (
    <div className="landing-search-widget">
      <div className="landing-search-bar">
        <svg className="landing-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          type="text"
          placeholder="Try it now — search &quot;community saturday&quot;, &quot;transport&quot;, &quot;daily activities&quot;..."
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </div>
      {results.length > 0 && (
        <div className="landing-search-results">
          {results.map((item) => (
            <div key={item.item_code} className="landing-search-result">
              <div className="landing-result-header">
                <span className="landing-result-code">{item.item_code}</span>
                {item.category && <span className="landing-result-category">{item.category}</span>}
              </div>
              <div className="landing-result-description">{item.description}</div>
              <div className="landing-result-meta">
                {item.day_type && <span className="landing-result-tag">{item.day_type}</span>}
                {item.time_type && <span className="landing-result-tag">{item.time_type}</span>}
              </div>
              <div className="landing-result-prices">
                {item.quote_based ? (
                  <span className="landing-result-quote">Quote-based — contact provider</span>
                ) : (
                  <>
                    <span className="landing-result-price">
                      <span className="landing-price-label">National</span>
                      <span className="landing-price-value">${item.national_price}<span className="landing-price-unit">/{item.unit}</span></span>
                    </span>
                    {item.remote_price && (
                      <span className="landing-result-price">
                        <span className="landing-price-label">Remote</span>
                        <span className="landing-price-value">${item.remote_price}<span className="landing-price-unit">/{item.unit}</span></span>
                      </span>
                    )}
                    {item.very_remote_price && (
                      <span className="landing-result-price">
                        <span className="landing-price-label">Very Remote</span>
                        <span className="landing-price-value">${item.very_remote_price}<span className="landing-price-unit">/{item.unit}</span></span>
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
          {query.length >= 2 && (
            <div className="landing-search-footer">
              <Link to="/login" className="landing-search-cta">
                Sign up free to save rates to your shifts & invoices →
              </Link>
            </div>
          )}
        </div>
      )}
      {query.length >= 2 && results.length === 0 && (
        <div className="landing-search-results">
          <div className="landing-search-empty">No results for "{query}" — try different keywords</div>
        </div>
      )}
    </div>
  );
}

function LandingPage() {
  const pageRef = useFadeIn();

  return (
    <div className="landing-page" ref={pageRef}>
      {/* Nav */}
      <nav className="landing-nav">
        <img src="/Roundloginlogo.png" alt="Easy Invoice" className="landing-nav-logo" />
        <div className="landing-nav-links">
          <Link to="/login">Log In</Link>
          <Link to="/login" className="btn btn-primary btn-small">Create Free Account</Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="landing-hero fade-in">
        <h1>The NDIS admin tool that gets out of your way</h1>
        <p className="landing-hero-sub">
          You'll need it, but barely use it.
        </p>
        <p>
          Clients, shifts, invoices, service agreements, NDIS pricing — all in one place.
          Built by a support worker who got sick of spreadsheets.
        </p>
        <div className="landing-hero-ctas">
          <a href="#price-guide" className="btn btn-primary">Try the NDIS Price Search ↓</a>
          <Link to="/login" className="btn btn-secondary">Create Free Account</Link>
        </div>
        <div className="landing-hero-trust">
          <span>🇦🇺 Built in Australia</span>
          <span>🔒 Data hosted in Australia</span>
          <span>No credit card required</span>
        </div>
      </section>

      {/* Problem */}
      <section className="landing-section landing-problem fade-in">
        <h2>The admin side of support work is a pain</h2>
        <p>
          You know the drill. Spreadsheets for shifts, a Word doc for invoices,
          a 100-page PDF for NDIS pricing, and a blank page when someone asks
          for a service agreement. It shouldn't take longer to invoice a shift
          than it does to work one.
        </p>
        <p>
          Easy Invoice puts it all in one place. Log a shift in 20 seconds,
          generate an invoice in 60, and never open that NDIS price guide PDF again.
        </p>
      </section>

      {/* Features */}
      <section className="landing-section fade-in">
        <h2>Everything in one place</h2>
        <div className="landing-features-grid">
          <div className="landing-feature-card">
            <div className="landing-feature-icon">👥</div>
            <h3>Client Management</h3>
            <p>
              Track participants, NDIS numbers, plan managers, and contact details.
              Everything organised and easy to find when you need it.
            </p>
          </div>
          <div className="landing-feature-card">
            <div className="landing-feature-icon">⏱️</div>
            <h3>Shift Tracking</h3>
            <p>
              Log hours, services, and travel — all linked to current NDIS rates.
              No more manual calculations or second-guessing pricing.
            </p>
          </div>
          <div className="landing-feature-card">
            <div className="landing-feature-icon">📄</div>
            <h3>Invoicing</h3>
            <p>
              Generate NDIS-compliant invoices instantly. No more fiddling with
              templates or spreadsheets — just accurate invoices, ready to send.
            </p>
          </div>
          <div className="landing-feature-card">
            <div className="landing-feature-icon">📝</div>
            <h3>Service Agreements</h3>
            <p>
              Create professional service agreements in minutes, not hours.
              Pre-built templates with NDIS pricing already included.
            </p>
          </div>
        </div>
      </section>

      {/* Speed Stats */}
      <section className="landing-section landing-speed fade-in">
        <h2>Fast enough to time it</h2>
        <p>We're not just saying it's quick. We measured it.</p>
        <div className="landing-speed-grid">
          <div className="landing-speed-card">
            <span className="landing-speed-number">20</span>
            <span className="landing-speed-unit">seconds</span>
            <span className="landing-speed-label">to log a shift</span>
          </div>
          <div className="landing-speed-card">
            <span className="landing-speed-number">60</span>
            <span className="landing-speed-unit">seconds</span>
            <span className="landing-speed-label">to generate a participant service agreement from scratch</span>
          </div>
        </div>
        <p className="landing-speed-cta">Don't believe us? <Link to="/login">Create a free account and try it yourself.</Link></p>
      </section>

      {/* Price Guide */}
      <section className="landing-price-guide fade-in" id="price-guide">
        <div className="landing-price-guide-badge">The only tool of its kind</div>
        <h2>Instant NDIS Price Guide Search</h2>
        <p>
          Type what you're looking for — no item codes, no 100-page PDFs.
          Just plain English and instant results.
        </p>
        <div className="landing-search-examples">
          <p className="landing-search-examples-title">Try typing:</p>
          <ul>
            <li><strong>"com sat"</strong> → Community Access — Saturday</li>
            <li><strong>"sle"</strong> → Sleepover shifts</li>
            <li><strong>"ev"</strong> → Evening rates</li>
            <li><strong>"meal"</strong> → Meal preparation</li>
            <li><strong>"tran"</strong> → Transport services</li>
          </ul>
        </div>
        <PriceGuideSearch />
      </section>

      {/* Credibility */}
      <section className="landing-section landing-credibility fade-in">
        <div className="landing-credibility-inner">
          <div className="landing-credibility-text">
            <h2>Not born in a boardroom</h2>
            <p className="landing-credibility-quote">
              "Easy Invoice was born in my bedroom. I was doing support work during the day
              and spending my evenings wrestling with spreadsheets and 100-page NDIS PDFs.
              I figured if I was going to waste that much time on admin, I might as well
              build something that does it for me."
            </p>
            <p>
              I'm Daniel — sole trader support worker, 5 years in the NDIS space.
              Every feature in this app exists because I needed it myself.
            </p>
            <div className="landing-credibility-badges">
              <span className="badge">🇦🇺 Australian Made</span>
              <span className="badge">5+ Years NDIS Experience</span>
              <span className="badge">Built for Sole Traders</span>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Comparison */}
      <section className="landing-section landing-pricing fade-in" id="pricing">
        <h2>Simple, transparent pricing</h2>
        <p>Start free. Upgrade when you're ready. No lock-in contracts.</p>

        {/* Comparison Table */}
        <div className="landing-pricing-table-wrap">
          <table className="landing-pricing-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>Free</th>
                <th className="landing-table-pro">Pro</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Service agreements</td><td><span className="landing-cross">✗</span></td><td><span className="landing-check">✓</span> Included</td></tr>
              <tr><td>Auto-invoicing (SMS approval)</td><td><span className="landing-cross">✗</span></td><td><span className="landing-check">✓</span> Included</td></tr>
              <tr><td>KM tracking</td><td><span className="landing-cross">✗</span></td><td><span className="landing-check">✓</span> Included</td></tr>
              <tr><td>Clients</td><td>Up to 2</td><td><span className="landing-check">✓</span> Unlimited</td></tr>
              <tr><td>Shifts per month</td><td>Up to 10</td><td><span className="landing-check">✓</span> Unlimited</td></tr>
              <tr><td>Invoices</td><td>Basic</td><td><span className="landing-check">✓</span> Unlimited</td></tr>
              <tr><td>NDIS Price Guide search</td><td><span className="landing-check">✓</span> Included</td><td><span className="landing-check">✓</span> Included</td></tr>
              <tr><td>All future features</td><td><span className="landing-cross">✗</span></td><td><span className="landing-check">✓</span> Included</td></tr>
            </tbody>
          </table>
        </div>

        <div className="landing-pricing-cards">
          <div className="landing-pricing-card">
            <div className="landing-pricing-card-header">
              <h3>Free</h3>
              <div className="landing-pricing-price">
                <span className="landing-pricing-amount">$0</span>
                <span className="landing-pricing-period">/month</span>
              </div>
              <p>Get started with the basics</p>
            </div>
            <p className="landing-pricing-upgrade-note">Upgrade to Pro anytime from inside the app</p>
            <Link to="/login" className="btn btn-secondary landing-pricing-btn">Create Free Account</Link>
          </div>
          <div className="landing-pricing-card landing-pricing-featured">
            <div className="landing-pricing-badge">Recommended for active support workers</div>
            <div className="landing-pricing-card-header">
              <h3>Pro</h3>
              <div className="landing-pricing-price">
                <span className="landing-pricing-amount">$19.99</span>
                <span className="landing-pricing-period">/month</span>
              </div>
              <p className="landing-pricing-value">One shift per month covers this</p>
            </div>
            <p className="landing-pricing-save">Save hours of admin every week</p>
            <Link to="/login" className="btn btn-primary landing-pricing-btn">Start Free, Unlock Pro Features</Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="landing-final-cta fade-in">
        <h2>Ready to simplify your NDIS business?</h2>
        <p className="landing-final-sub">Join support workers across Australia who are saving hours every week.</p>
        <Link to="/login" className="btn btn-primary">Create Free Account</Link>
        <p className="landing-final-reassurance">No credit card required · Set up in under 2 minutes · Cancel anytime</p>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-footer-inner">
          <div className="landing-footer-about">
            <p>
              Easy Invoice is a free NDIS business management tool for Australian sole trader
              support workers. Generate NDIS invoice templates, create NDIS service agreement
              templates, manage NDIS provider invoicing, and access essential NDIS support
              worker tools — all in one place.
            </p>
          </div>
          <div className="landing-footer-links">
            <h4>Quick Links</h4>
            <ul>
              <li><Link to="/login">Create Free Account</Link></li>
              <li><a href="#price-guide">NDIS Price Guide</a></li>
              <li><Link to="/login">Log In</Link></li>
            </ul>
          </div>
        </div>
        <div className="landing-footer-copy">
          © {new Date().getFullYear()} Easy Invoice. Built for NDIS sole traders in Australia.
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
